// aroInsightsApi.js

import { createApi, fetchBaseQuery, retry } from "@reduxjs/toolkit/query/react";
import { URLAPI } from "../../configuration";

const baseQuery = retry(
    fetchBaseQuery({
        baseUrl: URLAPI,
        prepareHeaders: (headers, { getState }) => {
            const token = getState().auth.user?.jwt;

            if (token) {
                headers.set("Authorization", `Bearer ${token}`);
            }

            return headers;
        },
    }),
    { maxRetries: 2 },
);

export const aroInsightsApi = createApi({
    reducerPath: "aroInsightsApi",
    baseQuery,
    tagTypes: ["AroInsights"],
    endpoints: (builder) => ({
        getAroInsights: builder.query({
            async queryFn(hotelID, _queryApi, _extraOptions, fetchWithBQ) {
                const now = new Date();

                const oldRange = {
                    from: new Date(
                        Date.UTC(now.getUTCFullYear() - 2, 0, 1),
                    ).toISOString(),
                    to: new Date(
                        Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), 1),
                    ).toISOString(),
                };

                const recentRange = {
                    from: oldRange.to,
                    to: new Date(
                        Date.UTC(
                            now.getUTCFullYear(),
                            now.getUTCMonth(),
                            now.getUTCDate() + 1,
                        ),
                    ).toISOString(),
                };

                const [
                    lostRevenueOld,
                    lostRevenueRecent,
                    allSearchesOld,
                    allSearchesRecent,
                ] = await Promise.all([
                    fetchWithBQ({
                        url: `/lost_revenue/summary/${hotelID}`,
                        method: "POST",
                        body: oldRange,
                    }),
                    fetchWithBQ({
                        url: `/lost_revenue/summary/${hotelID}`,
                        method: "POST",
                        body: recentRange,
                    }),
                    fetchWithBQ({
                        url: `/search_requests/summary/${hotelID}`,
                        method: "POST",
                        body: oldRange,
                    }),
                    fetchWithBQ({
                        url: `/search_requests/summary/${hotelID}`,
                        method: "POST",
                        body: recentRange,
                    }),
                ]);

                const failedRequest = [
                    lostRevenueOld,
                    lostRevenueRecent,
                    allSearchesOld,
                    allSearchesRecent,
                ].find((request) => request.error);

                if (failedRequest) {
                    return { error: failedRequest.error };
                }

                return {
                    data: {
                        LostRevenueSummary: [
                            ...(lostRevenueOld.data?.data ?? []),
                            ...(lostRevenueRecent.data?.data ?? []),
                        ],
                        AllSearches: [
                            ...(allSearchesOld.data?.data ?? []),
                            ...(allSearchesRecent.data?.data ?? []),
                        ],
                    },
                };
            },
            providesTags: (_result, _error, hotelID) => [
                {
                    type: "AroInsights",
                    id: hotelID,
                },
            ],
            keepUnusedDataFor: 60 * 30,
        }),
    }),
});

export const { useGetAroInsightsQuery } = aroInsightsApi;
